import { Observable, of } from 'rxjs';
import { Action } from '@ngrx/store';
import { createEffect, ofType, Actions } from '@ngrx/effects';
import { concatLatestFrom } from '@ngrx/operators';
import {
    filter,
    first,
    map,
    mergeMap,
    switchMap,
    tap,
    catchError,
    defaultIfEmpty,
    last,
} from 'rxjs/operators';
import { Injectable, OnDestroy, inject } from '@angular/core';
import * as fromActions from './dynamic.actions';
import { DynamicUiService } from '../services/dynamic-ui.service';
import { DynamicFacade } from './dynamic.facade';
import { NotifierService } from 'src/app/app/services/notifier.service';
import { result } from 'lodash-es';
import { UiDefinition } from '../models/ui-definition.models';
import { GetActionsFromUiDefinition } from 'src/app/shared/helpers/store-helper';
import { UiActionStore } from '../models/ui-action.models';
import { SubscriptionManagerService } from './subscription-manager.service';

@Injectable({
    providedIn: 'root',
})
export class DynamicEffects implements OnDestroy {
    constructor(
        private actions: Actions,
        private dynamicUiService: DynamicUiService,
        private notifierService: NotifierService,
        private dynamicFacade: DynamicFacade,
        private subscriptionManager: SubscriptionManagerService,
    ) {}

    ngOnDestroy(): void {
        this.subscriptionManager.UnsubscribeActions();
    }

    // Beim hinzufügen der Action zum Store wird geprüft, ob das Attribute executeOnInit auf true ist und führt die Action aus.
    public initLocalStore = createEffect(
        (actions$ = inject(Actions), facade = inject(DynamicFacade)) =>
            actions$.pipe(
                ofType(fromActions.initLocalStore, fromActions.initChildStore),
                concatLatestFrom(() => facade.actions$),
                tap(([action, actionsFromStore]) => {
                    of(GetActionsFromUiDefinition(action.uiComponents))
                        .pipe(
                            filter((actions) => actions.length > 0),
                            map((uiActions) => {
                                return uiActions.filter(
                                    (item) => item.executeOnInit === true,
                                );
                            }),
                            defaultIfEmpty([]),
                            first(),
                            tap((uiActions) => {
                                return uiActions.map((uiAction) =>
                                    this.dynamicFacade.ExecuteAction(
                                        uiAction.name,
                                        action.storeDataChildNames,
                                        'ExecuteOnInit: ' + uiAction.name,
                                    ),
                                );
                            }),
                        )
                        .subscribe();
                }),
                tap(([action, actionsFromStore]) => {
                    of(GetActionsFromUiDefinition(action.uiComponents))
                        .pipe(
                            filter((actions) => actions.length > 0),
                            map((uiActions) => {
                                return uiActions.filter(
                                    (item) =>
                                        item.executeOnDataStoresChanged &&
                                        item.executeOnDataStoresChanged.length >
                                            0,
                                );
                            }),
                            defaultIfEmpty([]),
                            //first(),
                            tap((uiActions) => {
                                return uiActions.map((uiAction) => {
                                    //console.log('*1* Subscribing to data store changes for action:', uiAction.name, 'with executeOnDataStoresChanged:', uiAction.executeOnDataStoresChanged);
                                    //this.UnsubscribeOneAction(uiAction);
                                    uiAction.executeOnDataStoresChanged.forEach(
                                        (item: string) => {
                                            //console.log('*2* Subscribing to data store changes for store:', item, 'for action:', uiAction.name);
                                            if (item) {
                                                //console.log('*3* Subscribing to data store changes for store:', item, 'for action:', uiAction.name);
                                                const actionSubScription =
                                                    this.dynamicUiService
                                                        .GetValueAsObservable(
                                                            item,
                                                            action.storeDataChildNames,
                                                        )
                                                        .subscribe(
                                                            (itemValue) => {
                                                                //console.log('*4* Data store change detected for store:', item, 'for action:', uiAction.name, 'with value:', itemValue);
                                                                if (itemValue) {
                                                                    this.dynamicFacade.ExecuteAction(
                                                                        uiAction.name,
                                                                        action.storeDataChildNames,
                                                                        'DataStoreChanged: ' +
                                                                            item,
                                                                    );
                                                                }
                                                            },
                                                        );
                                                this.subscriptionManager.AddSubscription(
                                                    action.storeDataChildNames.join(
                                                        '.',
                                                    ),
                                                    uiAction.name,
                                                    actionSubScription,
                                                );
                                            }
                                        },
                                    );
                                });
                            }),
                        )
                        .subscribe();
                }),
            ),
        { dispatch: false },
    );

    // Führt eine Action
    public executeAction: Observable<Action> = createEffect(
        () =>
            this.actions.pipe(
                ofType(fromActions.executeAction),
                tap((action) => {
                    this.dynamicFacade
                        .GetAction(
                            action.actionName,
                            action.storeDataChildNames,
                        )
                        .pipe(
                            first(),
                            tap((uiAction) => {
                                if (
                                    uiAction?.removeStoresBeforExecute &&
                                    uiAction?.removeStoresBeforExecute.length >
                                        0
                                ) {
                                    uiAction.removeStoresBeforExecute.forEach(
                                        (storeName) => {
                                            this.dynamicUiService.RemoveValue(
                                                storeName,
                                                action.storeDataChildNames,
                                            );
                                        },
                                    );
                                }
                            }),
                        )
                        .subscribe();
                }),
                mergeMap((action) =>
                    this.dynamicUiService
                        .ExecuteActionFromName(
                            action.actionName,
                            action.storeDataChildNames,
                        )
                        .pipe(
                            first(),
                            map((uiAction): any => {
                                var actionPath =
                                    action.actionPath +
                                    ' -> ' +
                                    action.actionName;
                                console.log('execAction', actionPath); // Ausgabe des Action-Pfads in der Konsole
                                return fromActions.executeActionSuccess({
                                    uiAction: uiAction,
                                    storeDataChildNames:
                                        action.storeDataChildNames,
                                    actionPath: actionPath,
                                });
                            }),
                        ),
                ),
            )
    );

    // Wird ausgeführt, wenn eine Action erfolgreich ausgeführt wurde
    public executeActionSuccess: Observable<Action> = createEffect(
        () =>
            this.actions.pipe(
                ofType(fromActions.executeActionSuccess),
                tap((action) => {
                    if (!action.uiAction?.startAnotherAction) {
                        if (action.uiAction?.successMessage) {
                            this.notifierService.showNotificationSuccess(
                                action.uiAction.successMessage,
                            );
                        }
                        if (
                            action.uiAction?.executeAfter != undefined &&
                            action.uiAction?.executeAfter.length > 0
                        ) {
                            action.uiAction.executeAfter.forEach((c) => {
                                this.dynamicFacade.ExecuteAction(
                                    c,
                                    action.storeDataChildNames,
                                    action.actionPath,
                                );
                            });
                        }
                    }
                }),
            ),
        { dispatch: false },
    );

    // Wird ausgeführt, wenn eine Action nicht erfolgreich ausgeführt werden konnte
    public executeActionFailed: Observable<Action> = createEffect(
        () =>
            this.actions.pipe(
                ofType(fromActions.executeActionFailed),
                tap((action) => {
                    this.notifierService.showNotificationError(
                        action.uiAction?.errorMessage ||
                            'Ein Fehler ist aufgetreten' +
                                (action.uiAction?.systemErrorMessage
                                    ? '/r/n' +
                                      action.uiAction.systemErrorMessage
                                    : ''),
                    );
                    console.error(
                        `Error in Action ${action.uiAction.name}: `,
                        action.uiAction?.systemErrorMessage ||
                            'unbekannter Fehler',
                    );
                }),
            ),
        { dispatch: false },
    );

    public executeStoredProcedure: Observable<Action> = createEffect(() =>
        this.actions.pipe(
            ofType(fromActions.executeStoredProcedure),
            mergeMap((loadData) =>
                this.dynamicUiService
                    .ActionExecuteStoredProcedure(
                        loadData.uiAction,
                        loadData.storeDataChildNames,
                    )
                    .pipe(
                        map((result) => {
                            if (result.success && result.success == true) {
                                const json = JSON.parse(result?.result ?? '{}');
                                this.dynamicUiService.SetValue(
                                    loadData.uiAction.config.targetStoreData,
                                    loadData.storeDataChildNames,
                                    json,
                                    loadData.uiAction.name,
                                );
                                loadData.uiAction.startAnotherAction = false;
                                return fromActions.executeActionSuccess({
                                    uiAction: loadData.uiAction,
                                    storeDataChildNames:
                                        loadData.storeDataChildNames,
                                    actionPath:
                                        loadData.actionPath +
                                        ' -> ' +
                                        loadData.uiAction.name,
                                });
                            } else {
                                let errorMessge: string =
                                    result.errorCode +
                                    ': ' +
                                    result.errorMessage;
                                this.notifierService.showNotificationError(
                                    errorMessge,
                                );
                                console.error(
                                    'Error in executeStoredProcedure: ',
                                    errorMessge,
                                );
                                return fromActions.executeActionFailed({
                                    uiAction: loadData.uiAction,
                                    storeDataChildNames:
                                        loadData.storeDataChildNames,
                                    actionPath:
                                        loadData.actionPath +
                                        ' -> ' +
                                        loadData.uiAction.name,
                                });
                            }
                        }),
                        catchError((error) => {
                            loadData.uiAction.startAnotherAction = false;
                            loadData.uiAction.systemErrorMessage =
                                error?.message ||
                                'Ein unbekannter Fehler ist aufgetreten.';
                            this.notifierService.showNotificationError(error);
                            console.error(
                                'Error in executeStoredProcedure: ',
                                error,
                            );
                            return of(
                                fromActions.executeActionFailed({
                                    uiAction: loadData.uiAction,
                                    storeDataChildNames:
                                        loadData.storeDataChildNames,
                                    actionPath:
                                        loadData.actionPath +
                                        ' -> ' +
                                        loadData.uiAction.name,
                                }),
                            );
                        }),
                    ),
            ),
        ),
    );

    public generatePDF: Observable<Action> = createEffect(() =>
        this.actions.pipe(
            ofType(fromActions.generatePDF),
            mergeMap((loadData) =>
                this.dynamicUiService
                    .ActionGeneratePDF(
                        loadData.uiAction,
                        loadData.storeDataChildNames,
                    )
                    .pipe(
                        map((result) => {
                            if (result.success && result.success == true) {
                                const json = { pdf: result?.result };
                                this.dynamicUiService.SetValue(
                                    loadData.uiAction.config.targetStoreData,
                                    loadData.storeDataChildNames,
                                    json,
                                    loadData.uiAction.name,
                                );
loadData.uiAction.startAnotherAction = false;
                                return fromActions.executeActionSuccess({
                                    uiAction: loadData.uiAction,
                                    storeDataChildNames:
                                        loadData.storeDataChildNames,
                                    actionPath:
                                        loadData.actionPath +
                                        ' -> ' +
                                        loadData.uiAction.name,
                                });
                            } else {
                                let errorMessge: string =
                                    result.errorCode +
                                    ': ' +
                                    result.errorMessage;
                                loadData.uiAction.startAnotherAction = false;
                                loadData.uiAction.systemErrorMessage =
                                    errorMessge;
                                return fromActions.executeActionFailed({
                                    uiAction: loadData.uiAction,
                                    storeDataChildNames:
                                        loadData.storeDataChildNames,
                                    actionPath:
                                        loadData.actionPath +
                                        ' -> ' +
                                        loadData.uiAction.name,
                                });
                            }
                        }),
                        catchError((error) => {
                            loadData.uiAction.startAnotherAction = false;
                            loadData.uiAction.systemErrorMessage = error;
                            return of(
                                fromActions.executeActionFailed({
                                    uiAction: loadData.uiAction,
                                    storeDataChildNames:
                                        loadData.storeDataChildNames,
                                    actionPath:
                                        loadData.actionPath +
                                        ' -> ' +
                                        loadData.uiAction.name,
                                }),
                            );
                        }),
                    ),
            ),
        ),
    );

    public sendMail: Observable<Action> = createEffect(() =>
        this.actions.pipe(
            ofType(fromActions.sendMail),
            mergeMap((loadData) =>
                this.dynamicUiService
                    .ActionSendMail(
                        loadData.uiAction,
                        loadData.storeDataChildNames,
                    )
                    .pipe(
                        map((result) => {
                            if (result.success && result.success == true) {
                                loadData.uiAction.startAnotherAction = false;
                                return fromActions.executeActionSuccess({
                                    uiAction: loadData.uiAction,
                                    storeDataChildNames:
                                        loadData.storeDataChildNames,
                                    actionPath:
                                        loadData.actionPath +
                                        ' -> ' +
                                        loadData.uiAction.name,
                                });
                            } else {
                                let errorMessge: string =
                                    result.errorCode +
                                    ': ' +
                                    result.errorMessage;
                                loadData.uiAction.startAnotherAction = false;
                                loadData.uiAction.systemErrorMessage =
                                    errorMessge;
                                return fromActions.executeActionFailed({
                                    uiAction: loadData.uiAction,
                                    storeDataChildNames:
                                        loadData.storeDataChildNames,
                                    actionPath:
                                        loadData.actionPath +
                                        ' -> ' +
                                        loadData.uiAction.name,
                                });
                            }
                        }),
                        catchError((error) => {
                            loadData.uiAction.startAnotherAction = false;
                            loadData.uiAction.systemErrorMessage = error;
                            return of(
                                fromActions.executeActionFailed({
                                    uiAction: loadData.uiAction,
                                    storeDataChildNames:
                                        loadData.storeDataChildNames,
                                    actionPath:
                                        loadData.actionPath +
                                        ' -> ' +
                                        loadData.uiAction.name,
                                }),
                            );
                        }),
                    ),
            ),
        ),
    );

    public exportCsv: Observable<Action> = createEffect(() => {
        const b64toBlob = (
            b64Data: string,
            contentType = '',
            sliceSize = 512,
        ) => {
            const byteCharacters = atob(b64Data);
            const byteArrays = [];

            for (
                let offset = 0;
                offset < byteCharacters.length;
                offset += sliceSize
            ) {
                const slice = byteCharacters.slice(offset, offset + sliceSize);

                const byteNumbers = new Array(slice.length);
                for (let i = 0; i < slice.length; i++) {
                    byteNumbers[i] = slice.charCodeAt(i);
                }

                const byteArray = new Uint8Array(byteNumbers);
                byteArrays.push(byteArray);
            }

            const blob = new Blob(byteArrays, { type: contentType });
            return blob;
        };

        return this.actions.pipe(
            ofType(fromActions.exportCsv),
            mergeMap((loadData) =>
                this.dynamicUiService
                    .ActionExportCsv(
                        loadData.uiAction,
                        loadData.storeDataChildNames,
                    )
                    .pipe(
                        map((response) => {
                            if (response?.success && response.success == true) {
                                const blob = b64toBlob(
                                    response.result.fileContent,
                                    response.result.fileType || 'text/csv',
                                );
                                const url = window.URL.createObjectURL(blob);
                                const a = document.createElement('a');
                                a.href = url;
                                a.download =
                                    loadData.uiAction.config?.fileName ||
                                    response.result.fileName ||
                                    'exportData.csv';
                                document.body.appendChild(a);
                                a.click();
                                document.body.removeChild(a);
                                window.URL.revokeObjectURL(url);
                                loadData.uiAction.startAnotherAction = false;
                                return fromActions.executeActionSuccess({
                                    uiAction: loadData.uiAction,
                                    storeDataChildNames:
                                        loadData.storeDataChildNames,
                                    actionPath:
                                        loadData.actionPath +
                                        ' -> ' +
                                        loadData.uiAction.name,
                                });
                            } else {
                                this.notifierService.showNotificationError(
                                    response?.errorMessage ||
                                        'Fehler beim Exportieren der CSV-Datei.',
                                );
                                return fromActions.executeActionFailed({
                                    uiAction: loadData.uiAction,
                                    storeDataChildNames:
                                        loadData.storeDataChildNames,
                                    actionPath:
                                        loadData.actionPath +
                                        ' -> ' +
                                        loadData.uiAction.name,
                                });
                            }
                        }),
                        catchError((error) => {
                            loadData.uiAction.startAnotherAction = false;
                            loadData.uiAction.systemErrorMessage = error;
                            return of(
                                fromActions.executeActionFailed({
                                    uiAction: loadData.uiAction,
                                    storeDataChildNames:
                                        loadData.storeDataChildNames,
                                    actionPath:
                                        loadData.actionPath +
                                        ' -> ' +
                                        loadData.uiAction.name,
                                }),
                            );
                        }),
                    ),
            ),
        );
    });

    public getUiDefintionFromUrl: Observable<Action> = createEffect(() =>
        this.actions.pipe(
            ofType(fromActions.getUiDefinitionFromUrl),
            mergeMap((loadData) =>
                this.dynamicUiService
                    .GetUiDefinitionFromUrl(
                        loadData.url,
                        loadData.currentPersonId,
                        loadData.currentVereinId,
                        loadData.currentAnlassId,
                    )
                    .pipe(
                        map((result) => {
                            if (result.success && result.success == true) {
                                //console.log('#### UI Definition loaded successfully for URL:', loadData.url);
                                return fromActions.getUiDefinitionSuccess({
                                    info: loadData.url,
                                    uiDefinition: JSON.parse(
                                        result.result,
                                    ) as UiDefinition,
                                });
                            } else {
                                let errorMessge: string =
                                    result.errorCode +
                                    ': ' +
                                    result.errorMessage;
                                this.notifierService.showNotificationError(
                                    errorMessge,
                                );
                                console.error(
                                    'Error in getUiDefintion: ',
                                    errorMessge,
                                );
                                return fromActions.getUiDefinitionFailed({
                                    info: loadData.url,
                                });
                            }
                        }),
                        catchError((error) => {
                            this.notifierService.showNotificationError(error);
                            console.error('Error in getUiDefintion: ', error);
                            return of(
                                fromActions.getUiDefinitionFailed({
                                    info: loadData.url,
                                }),
                            );
                        }),
                    ),
            ),
        ),
    );

    public getUiDefintionFromName: Observable<Action> = createEffect(() =>
        this.actions.pipe(
            ofType(fromActions.getUiDefinitionFromName),
            mergeMap((loadData) =>
                this.dynamicUiService
                    .GetUiDefinitionFromName(
                        loadData.name,
                        loadData.currentPersonId,
                        loadData.currentVereinId,
                        loadData.currentAnlassId,
                    )
                    .pipe(
                        map((result) => {
                            if (result.success && result.success == true) {
                                //console.log('#### UI Definition loaded successfully for name:', loadData.name);
                                return fromActions.getUiDefinitionSuccess({
                                    info: loadData.name,
                                    uiDefinition: JSON.parse(
                                        result.result,
                                    ) as UiDefinition,
                                });
                            } else {
                                let errorMessge: string =
                                    result.errorCode +
                                    ': ' +
                                    result.errorMessage;
                                this.notifierService.showNotificationError(
                                    errorMessge,
                                );
                                console.error(
                                    'Error in getUiDefintion: ',
                                    errorMessge,
                                );
                                return fromActions.getUiDefinitionFailed({
                                    info: loadData.name,
                                });
                            }
                        }),
                        catchError((error) => {
                            this.notifierService.showNotificationError(error);
                            console.error('Error in getUiDefintion: ', error);
                            return of(
                                fromActions.getUiDefinitionFailed({
                                    info: loadData.name,
                                }),
                            );
                        }),
                    ),
            ),
        ),
    );

    public getPresentation: Observable<Action> = createEffect(() =>
        this.actions.pipe(
            ofType(fromActions.getPresentation),
            mergeMap((loadData) =>
                this.dynamicUiService.GetPresentation(loadData.id).pipe(
                    map((result) => {
                        return fromActions.getPresentationSuccess({
                            presentation: result,
                        });
                    }),
                    catchError((error) => {
                        this.notifierService.showNotificationError(error);
                        console.error('Error in getPresentation: ', error);
                        return of(
                            fromActions.getPresentationFailed({
                                errorMsg: error,
                            }),
                        );
                    }),
                ),
            ),
        ),
    );
}
